import { Alert, Button, Input, Spinner, Switch } from '@heroui/react'
import { useMutation } from '@tanstack/react-query'
import { useAuth } from 'hooks/useAuth'
import { AlertTriangle, Mail } from 'lucide-react'
import { memo, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import { useOrgsBillingData } from 'utils/funcs/billing/getOrgsBillingData'

function CreditsNotification() {
  const { credits: currentCredits, org } = useAuth()

  const [customThreshold, setCustomThreshold] = useState(100)
  const [customAlertEnabled, setCustomAlertEnabled] = useState(false)
  const [zeroAlertEnabled, setZeroAlertEnabled] = useState(false)

  const orgCreditAlertRecipients = org?.preferences?.credit_alert_recipients

  const [emailAddresses, setEmailAddresses] = useState(orgCreditAlertRecipients || '')
  useEffect(() => {
    if (orgCreditAlertRecipients) {
      setEmailAddresses(orgCreditAlertRecipients)
    }
  }, [orgCreditAlertRecipients])

  const { mutate: updateOrgProperties } = useMutation({
    mutationKey: ['updateOrgProperties'],
    mutationFn: () => $fetch(`/orgs/${org?.id}/properties`, {
      method: 'PATCH',
      body: {
        updates: {
          preferences: {
            ...(org?.preferences || {}),
            credit_alert_recipients: emailAddresses || undefined,
          },
        },
      },
    }),
    enabled: !!emailAddresses && !!org?.id,
    onError: (error) => {
      console.error('Failed to update credit alert recipients:', error)
      toast.error('Failed to update credit alert recipients', { toastId: 'update-recipients' })
    },
  })

  const handleCustomSwitchChange = (checked) => {
    setCustomAlertEnabled(checked)
    if (!checked) {
      updateThresholds()
    }
  }

  const handleZeroSwitchChange = (checked) => {
    setZeroAlertEnabled(checked)
    updateThresholds()
  }

  const { data: billingData, isLoading: isLoadingBillingData } = useOrgsBillingData()
  useEffect(() => {
    if (!isLoadingBillingData && billingData?.custom_alert_threshold_value) {
      setCustomAlertEnabled(true)
      setCustomThreshold(billingData.custom_alert_threshold_value)
    }

    setZeroAlertEnabled(!!billingData?.zero_balance_alert_enabled)
  }, [isLoadingBillingData])

  const { mutate: updateThresholds, isPending: isUpdating } = useMutation({
    mutationKey: ['updateRechargeAmount'],
    mutationFn: () => $fetch('/billing/credit_threshold', {
      method: 'PATCH',
      body: {
        credit_threshold: customAlertEnabled ? customThreshold : null,
        zero_balance_alert_enabled: zeroAlertEnabled,
      },
    }),
    onSuccess: () => {
      if (customAlertEnabled) {
        toast.success('Credit threshold updated successfully', { toastId: 'update-threshold' })
      }
    },
    onError: (error) => {
      console.error('Failed to update credit threshold:', error)
      toast.error('Failed to update credit threshold', { toastId: 'update-threshold' })
    },
  })

  const isThresholdValid = customThreshold > 0 && customThreshold < currentCredits

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-semibold text-gray-900">Credit Alert Settings</h5>
      </div>
      <div className="h-px w-full bg-gray-200" />
      <p className="text-sm text-gray-500">
        Get notified when your credits are running low to avoid service interruptions.
      </p>

      <div className={`
        flex flex-col gap-y-2 rounded-lg border bg-gray-50 px-4 py-2.5
      `}
      >
        <div className="flex items-center gap-2">
          <Mail className="h-3.5 w-3.5 text-gray-500" />
          <h3 className="text-sm">Alert recipients</h3>
        </div>
        <Input
          description="You can enter mutliple email addresses separated by commas."
          onBlur={() => updateOrgProperties()}
          variant="bordered"
          className="min-w-56 gap-10 pt-1"
          classNames={{
            label: 'text-xs pb-1',
            inputWrapper: 'bg-white rounded-lg',
          }}
          radius="sm"
          labelPlacement="outside"
          errorMessage="You must have at least one, and up to 10 email address to alert"
          type="email"
          isInvalid={(!emailAddresses && (customAlertEnabled || zeroAlertEnabled) || emailAddresses.split(',').length > 10)}
          value={emailAddresses}
          onValueChange={setEmailAddresses}
          placeholder="e.g. dev@example.com, ops@example.com"
        />
      </div>

      <div className="flex flex-col gap-y-6 pt-1">
        <div className="flex items-center justify-center gap-2">
          <div className="flex w-full flex-col gap-y-1">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="text-sm font-medium">Critical Alert</span>
            </div>
            <p className="text-xs text-gray-500">
              Enable alerts when credits reach zero.
            </p>
          </div>
          {isLoadingBillingData
            ? <Spinner />
            : (
                <div className="flex items-center gap-5">
                  {zeroAlertEnabled ? 'Enabled' : 'Disabled'}
                  <Switch size="sm" isSelected={zeroAlertEnabled} onValueChange={handleZeroSwitchChange} />
                </div>
              )}
        </div>

        <div className="flex flex-col gap-y-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-y-1">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <span className="text-sm font-medium">Custom Alert</span>
              </div>
              <p className="text-xs text-gray-500">Set your own threshold for early warning notifications.</p>
            </div>
            {isLoadingBillingData
              ? <Spinner />
              : (
                  <div className="flex items-center gap-5">
                    {customAlertEnabled ? 'Enabled' : 'Disabled'}
                    <Switch size="sm" isSelected={customAlertEnabled} onValueChange={handleCustomSwitchChange} />
                  </div>
                )}
          </div>

          {customAlertEnabled && (
            <div className="border-muted flex flex-col gap-y-3 border-l-2 pl-6">
              <div className="flex flex-col gap-y-2">
                <span htmlFor="threshold" className="text-xs">
                  Alert threshold
                </span>
                <div className="flex items-center gap-3">
                  <Input
                    variant="bordered"
                    classNames={{
                      inputWrapper: 'rounded-lg',
                    }}
                    id="threshold"
                    type="number"
                    value={customThreshold}
                    onChange={e => setCustomThreshold(Number(e.target.value))}
                    className="w-32"
                    min="1"
                    max={currentCredits - 1}
                  />
                  <span className="text-muted-foreground text-xs">credits remaining</span>
                </div>
                {!isThresholdValid && customThreshold > 0 && (
                  <p className="text-xs whitespace-nowrap text-red-500">
                    {`Threshold must be less than your current balance (${currentCredits} credits)`}
                  </p>
                )}
              </div>

              <Alert
                className="w-fit"
                classNames={{
                  description: 'text-xs',
                  title: 'text-xs font-medium',
                  base: 'p-1.5 pr-3 items-center',
                  mainWrapper: 'ms-1 gap-0.5',
                }}
                title="Note"
                hideIconWrapper
                description={`You'll receive one email when credits drop below ${customThreshold}. No additional emails will be sent until your balance is topped up above this threshold.`}
                type="warning"
              />

              <Button
                onPress={updateThresholds}
                isLoading={isUpdating}
                className={`
                  max-w-auto inline-flex h-8 w-fit items-center justify-center
                  rounded border border-transparent bg-indigo-600 px-2.5 py-1.5
                  text-xs font-medium text-white shadow-xs transition
                  duration-150 ease-in-out
                  hover:bg-indigo-700
                  focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                  focus:outline-hidden
                `}
              >
                Save Changes
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default memo(CreditsNotification)
